// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — the whole rule set, pure and DOM-free. This file is the single source of truth: the
// board, legal plies, applying one, terminal detection, the seeded opening, and the packed wire
// encoding. The bot searches it, the balance sim plays it, and lockstep replays it — one generator,
// so a peer can never "validate" a move against rules the sender did not use.
//
// Two decisions carry the whole file:
//
//   1. LEGALITY IS THE PAIR, NOT THE MOVE. A step into a pocket where every neighbour is occupied
//      or domed is not a legal move, because you must build afterwards. genPlies() therefore emits
//      (builder, to, build) triples and never a bare step — filtering afterwards is how you ship a
//      game that lets you move into a dead end and then wedges.
//
//   2. NO SUBTRACTIVE POWER. Every legal turn raises the total height by at least one, and total
//      height is bounded by 4 × cells, so a game cannot exceed 4 × cells plies. That single fact
//      replaces a repetition rule, a draw rule, and a stalemate detector, and tests/mechanism
//      asserts it at zero tolerance.

import { makeRng, shuffle, type Rng } from '@ben-gy/game-engine/rng';
import { modeOf, type Mode } from './modes';

/** Bumped when anything here changes the meaning of a ply. Peers on different revisions refuse. */
export const RULES_REV = 1;

export const DOME = 4;
export const WIN_LEVEL = 3;
/** Sentinel for "no cell" in a packed field (6 bits, and no board reaches 63 cells). */
export const NONE = 63;

// ── powers ──────────────────────────────────────────────────────────────────────
export const P_NONE = 0;
export const P_MASON = 1;
export const P_WARDEN = 2;
export const P_GALE = 3;
export const P_COURIER = 4;
export const P_SCOUT = 5;

export interface PowerDef {
  id: number;
  key: string;
  name: string;
  text: string;
}

/**
 * Five verbs that do not collapse into one another: build more, deny, displace, reach further,
 * build elsewhere. Deliberately no "remove a block" — see the termination note at the top.
 */
export const POWERS: PowerDef[] = [
  { id: P_MASON, key: 'mason', name: 'Mason', text: 'Spend a charge to raise a second block, on another square beside the builder you moved.' },
  { id: P_WARDEN, key: 'warden', name: 'Warden', text: 'Spend a charge to cap an adjacent square with a dome, at any height, instead of building.' },
  { id: P_GALE, key: 'gale', name: 'Gale', text: 'Spend a charge to step into an adjacent rival and shove them one square further along the same line.' },
  { id: P_COURIER, key: 'courier', name: 'Courier', text: 'Spend a charge to move the same builder twice before building — never back where it started.' },
  { id: P_SCOUT, key: 'scout', name: 'Scout', text: 'Spend a charge to build beside your OTHER builder instead of the one that moved.' },
];

/**
 * Charges per tile. A single tunable table, so a nerf is one number rather than a rule rewrite —
 * and it needed to be one: measured over 220 neutral-draft games, Gale won 74.4% at three charges
 * while Warden won 37.1%, which is a dominant tile and a dead tile in the same pool. Shoving a
 * rival is a tempo gain AND a positional attack in one verb, so it is priced as a one-shot; doming
 * costs you the build that would have raised your own tower, so it is paid for with volume.
 */
export const CHARGES: Record<number, number> = {
  [P_MASON]: 3,
  [P_WARDEN]: 5,
  [P_GALE]: 2,
  [P_COURIER]: 3,
  [P_SCOUT]: 3,
};
export const chargesFor = (id: number): number => CHARGES[id] ?? 3;
export const MAX_CHARGES = 5;
export const DRAFT_ROW = 4;

export function powerOf(id: number): PowerDef | null {
  return POWERS.find((p) => p.id === id) ?? null;
}

// ── the opening, and the one constant fairness depends on ────────────────────────
/**
 * Placement priority and tempo are two separable goods, so they go to different seats: seat 0 gets
 * the first placement, seat 1 gets the first move. tests/balance.test.ts sweeps all four configs
 * over paired seeds; these are the ones that landed closest to even.
 */
export const PLACE_ORDER: readonly number[] = [0, 1, 0, 1];
export const FIRST_MOVER = 1;

// ── ply packing ─────────────────────────────────────────────────────────────────
// bits  0..5   a     primary cell (destination / placement cell / draft index)
// bits  6..11  b     build cell, or NONE
// bits 12..13  bi    which of the acting seat's builders
// bits 14..16  pw    power spent (0 = none)
// bits 17..22  aux   the power's second cell, or NONE
// bits 23..24  kind  0 = turn, 1 = place, 2 = draft
export const K_TURN = 0;
export const K_PLACE = 1;
export const K_DRAFT = 2;

export function packTurn(bi: number, to: number, build: number, pw = 0, aux = NONE): number {
  return ((to & 63) | ((build & 63) << 6) | ((bi & 3) << 12) | ((pw & 7) << 14) | ((aux & 63) << 17) | (K_TURN << 23)) >>> 0;
}
export function packPlace(cell: number): number {
  return ((cell & 63) | (NONE << 6) | (NONE << 17) | (K_PLACE << 23)) >>> 0;
}
export function packDraft(index: number): number {
  return ((index & 63) | (NONE << 6) | (NONE << 17) | (K_DRAFT << 23)) >>> 0;
}
export const plyKind = (p: number): number => (p >>> 23) & 3;
export const plyA = (p: number): number => p & 63;
export const plyBuild = (p: number): number => (p >>> 6) & 63;
export const plyBuilder = (p: number): number => (p >>> 12) & 3;
export const plyPower = (p: number): number => (p >>> 14) & 7;
export const plyAux = (p: number): number => (p >>> 17) & 63;

// ── phases ──────────────────────────────────────────────────────────────────────
export const PH_DRAFT = 0;
export const PH_PLACE = 1;
export const PH_PLAY = 2;
export const PH_OVER = 3;

export type WinReason = '' | 'climb' | 'sealed' | 'walkover' | 'nocontest';

export interface State {
  mode: Mode;
  seed: number;
  w: number;
  h: number;
  cells: number;
  /** 0..3 tower tiers, 4 = dome. */
  lev: Uint8Array;
  /** -1 empty, else the global builder index standing there. */
  occ: Int8Array;
  /** Cell per builder, -1 while unplaced. Builders 0..n-1 belong to seat 0, n..2n-1 to seat 1. */
  pos: Int8Array;
  /** Power id per seat, 0 = none. */
  power: Int8Array;
  /** Charges left per seat. */
  chg: Int8Array;
  /** The seeded draft row (power ids still on offer). */
  offer: number[];
  /** Placement seat order and who takes the first turn. Fields, not constants, so the balance
   *  sweep can play all four configs through the shipped code path rather than a copy of it. */
  placeOrder: readonly number[];
  firstMover: number;
  turn: number;
  phase: number;
  ply: number;
  winner: number;
  reason: WinReason;
  /** Every ply played, packed. The log IS the game — it is what crosses the wire. */
  log: number[];
  /** Per-seat running stats, for an honest results screen. */
  stats: SeatStats[];
}

export interface SeatStats {
  turns: number;
  blocks: number;
  domes: number;
  peak: number;
  highTurns: number;
  /** Sum of legal-ply counts at the start of each of this seat's turns, for a mobility average. */
  mobilitySum: number;
  mobilityN: number;
  charges: number;
}

const emptyStats = (): SeatStats => ({
  turns: 0,
  blocks: 0,
  domes: 0,
  peak: 0,
  highTurns: 0,
  mobilitySum: 0,
  mobilityN: 0,
  charges: 0,
});

// ── neighbour tables (per board size, computed once) ─────────────────────────────
const nbCache = new Map<string, Int8Array[]>();

export function neighbours(w: number, h: number): Int8Array[] {
  const key = `${w}x${h}`;
  const hit = nbCache.get(key);
  if (hit) return hit;
  const out: Int8Array[] = [];
  for (let i = 0; i < w * h; i++) {
    const x = i % w;
    const y = (i / w) | 0;
    const list: number[] = [];
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        list.push(ny * w + nx);
      }
    }
    out.push(Int8Array.from(list));
  }
  nbCache.set(key, out);
  return out;
}

/** The cell one further along the line from `from` through `to`, or -1 off the board. */
export function beyond(w: number, h: number, from: number, to: number): number {
  const fx = from % w;
  const fy = (from / w) | 0;
  const tx = to % w;
  const ty = (to / w) | 0;
  const nx = tx + (tx - fx);
  const ny = ty + (ty - fy);
  if (nx < 0 || ny < 0 || nx >= w || ny >= h) return -1;
  return ny * w + nx;
}

// ── construction ────────────────────────────────────────────────────────────────

/**
 * Seeded starting terrain, 180°-rotationally symmetric. Symmetric BY CONSTRUCTION is what makes a
 * random opening fair — neither seat can be handed the better board — and a board with structure on
 * it from frame one is also what teaches the height encoding before the player has read anything.
 */
export function layTerrain(state: State, rng: Rng): void {
  const cells = state.cells;
  const [pairs1, pairs2] = state.mode.terrain;
  const mate = (i: number): number => cells - 1 - i;
  const usable: number[] = [];
  for (let i = 0; i < cells; i++) if (mate(i) !== i) usable.push(i);
  const order = shuffle(rng, usable);
  const taken = new Set<number>();
  let laid1 = 0;
  let laid2 = 0;
  for (const i of order) {
    if (laid1 >= pairs1 && laid2 >= pairs2) break;
    if (taken.has(i) || taken.has(mate(i))) continue;
    const lv = laid2 < pairs2 ? 2 : 1;
    if (lv === 2) laid2++;
    else laid1++;
    state.lev[i] = lv;
    state.lev[mate(i)] = lv;
    taken.add(i);
    taken.add(mate(i));
  }
}

export function createState(modeId: string, seed: number): State {
  const mode = modeOf(modeId);
  const cells = mode.w * mode.h;
  const nBuilders = mode.builders * 2;
  const st: State = {
    mode,
    seed,
    w: mode.w,
    h: mode.h,
    cells,
    lev: new Uint8Array(cells),
    occ: new Int8Array(cells).fill(-1),
    pos: new Int8Array(nBuilders).fill(-1),
    power: Int8Array.from([P_NONE, P_NONE]),
    chg: Int8Array.from([0, 0]),
    offer: [],
    placeOrder: PLACE_ORDER,
    firstMover: FIRST_MOVER,
    turn: 0,
    phase: mode.powers ? PH_DRAFT : PH_PLACE,
    ply: 0,
    winner: -1,
    reason: '',
    log: [],
    stats: [emptyStats(), emptyStats()],
  };
  const rng = makeRng(seed >>> 0);
  layTerrain(st, rng);
  if (mode.powers) {
    st.offer = shuffle(
      rng,
      POWERS.map((p) => p.id),
    ).slice(0, DRAFT_ROW);
  }
  return st;
}

/** Override the opening configuration on a fresh state (the fairness sweep, and nothing else). */
export function chooseFirstFor(s: State, placeOrder: readonly number[] | null, firstMover: number | null): void {
  if (placeOrder) s.placeOrder = placeOrder.slice();
  if (firstMover !== null) s.firstMover = firstMover;
  if (s.phase === PH_PLACE) s.turn = s.placeOrder[0];
}

export function cloneState(s: State): State {
  return {
    ...s,
    lev: s.lev.slice(),
    occ: s.occ.slice(),
    pos: s.pos.slice(),
    power: s.power.slice(),
    chg: s.chg.slice(),
    offer: s.offer.slice(),
    log: s.log.slice(),
    stats: s.stats.map((x) => ({ ...x })),
  };
}

/** Builder indices belonging to a seat. */
export function buildersOf(s: State, seat: number): number[] {
  const n = s.mode.builders;
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(seat * n + i);
  return out;
}

export const seatOfBuilder = (s: State, b: number): number => (b < s.mode.builders ? 0 : 1);

// ── legal plies ─────────────────────────────────────────────────────────────────

function genDraft(s: State): number[] {
  return s.offer.map((_, i) => packDraft(i));
}

function genPlace(s: State): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.cells; i++) {
    if (s.occ[i] === -1 && s.lev[i] < DOME) out.push(packPlace(i));
  }
  return out;
}

/** True when a builder at `at` (having vacated `from`) has at least one legal build. */
function hasBuild(s: State, nb: Int8Array[], at: number, from: number): boolean {
  for (const c of nb[at]) {
    if (s.lev[c] >= DOME) continue;
    if (c === from) return true;
    if (s.occ[c] === -1) return true;
  }
  return false;
}

function pushBuilds(s: State, nb: Int8Array[], bi: number, to: number, from: number, out: number[]): void {
  for (const c of nb[to]) {
    if (s.lev[c] >= DOME) continue;
    if (c !== from && s.occ[c] !== -1) continue;
    out.push(packTurn(bi, to, c));
  }
}

/**
 * Every legal (builder, destination, build) triple for the seat to move, plus power variants while
 * charges remain. A winning ascent carries build = NONE: the rules say you do not build after
 * winning, so the encoding must not be able to express a phantom block on the final frame.
 */
export function genPlies(s: State): number[] {
  if (s.phase === PH_DRAFT) return genDraft(s);
  if (s.phase === PH_PLACE) return genPlace(s);
  if (s.phase !== PH_PLAY) return [];

  const nb = neighbours(s.w, s.h);
  const out: number[] = [];
  const mine = buildersOf(s, s.turn);
  const power = s.power[s.turn];
  const charged = s.chg[s.turn] > 0;

  for (let k = 0; k < mine.length; k++) {
    const b = mine[k];
    const from = s.pos[b];
    if (from < 0) continue;

    for (const to of nb[from]) {
      if (s.lev[to] >= DOME) continue;
      if (s.lev[to] > s.lev[from] + 1) continue;
      if (s.occ[to] !== -1) continue;

      if (s.lev[from] === WIN_LEVEL - 1 && s.lev[to] === WIN_LEVEL) {
        out.push(packTurn(k, to, NONE));
        continue;
      }
      if (!hasBuild(s, nb, to, from)) continue;
      pushBuilds(s, nb, k, to, from, out);

      if (charged && power === P_MASON) {
        // Two blocks beside the builder you moved. c < c2 dedupes the pair (order cannot matter —
        // they are different squares), which keeps the branching factor sane for the search.
        for (const c of nb[to]) {
          if (s.lev[c] >= DOME || (c !== from && s.occ[c] !== -1)) continue;
          for (const c2 of nb[to]) {
            if (c2 <= c) continue;
            if (s.lev[c2] >= DOME || (c2 !== from && s.occ[c2] !== -1)) continue;
            out.push(packTurn(k, to, c, P_MASON, c2));
          }
        }
      }
      if (charged && power === P_WARDEN) {
        for (const c of nb[to]) {
          if (s.lev[c] >= DOME || (c !== from && s.occ[c] !== -1)) continue;
          out.push(packTurn(k, to, c, P_WARDEN));
        }
      }
      if (charged && power === P_SCOUT) {
        for (const other of mine) {
          if (other === b) continue;
          const op = s.pos[other];
          if (op < 0) continue;
          for (const c of nb[op]) {
            // `to` is where this builder now stands, and it CAN be a neighbour of the other
            // builder — the occupancy array has not moved yet, so it must be excluded by hand.
            if (c === to) continue;
            if (s.lev[c] >= DOME || (c !== from && s.occ[c] !== -1)) continue;
            out.push(packTurn(k, to, c, P_SCOUT));
          }
        }
      }
      if (charged && power === P_COURIER) {
        // A second step from `to`, then build. `from` counts as empty (we left it).
        for (const t2 of nb[to]) {
          if (t2 === from) continue;
          if (s.lev[t2] >= DOME) continue;
          if (s.lev[t2] > s.lev[to] + 1) continue;
          if (s.occ[t2] !== -1) continue;
          if (s.lev[to] === WIN_LEVEL - 1 && s.lev[t2] === WIN_LEVEL) {
            out.push(packTurn(k, t2, NONE, P_COURIER, to));
            continue;
          }
          // No hasBuild() pre-check here: it does not know the intermediate square was vacated
          // too, so it would drop the case where that square is the only legal build.
          for (const c of nb[t2]) {
            if (s.lev[c] >= DOME) continue;
            if (c !== from && c !== to && s.occ[c] !== -1) continue;
            out.push(packTurn(k, t2, c, P_COURIER, to));
          }
        }
      }
    }

    if (charged && power === P_GALE) {
      for (const to of nb[from]) {
        if (s.lev[to] >= DOME) continue;
        // A shove may not be used to CLIMB. Without this, Gale's real verb is "displace a rival off
        // the high ground and take it", which measured 74% against the rest of the pool: it was
        // buying tempo and position and an ascent in one charge. Level or downhill only.
        if (s.lev[to] > s.lev[from]) continue;
        const victim = s.occ[to];
        if (victim === -1 || seatOfBuilder(s, victim) === s.turn) continue;
        const push = beyond(s.w, s.h, from, to);
        if (push < 0 || s.lev[push] >= DOME || s.occ[push] !== -1) continue;
        if (s.lev[from] === WIN_LEVEL - 1 && s.lev[to] === WIN_LEVEL) {
          out.push(packTurn(k, to, NONE, P_GALE));
          continue;
        }
        for (const c of nb[to]) {
          if (s.lev[c] >= DOME) continue;
          if (c === push) continue; // the shoved rival lands there — it is occupied, not free
          if (c !== from && s.occ[c] !== -1) continue;
          out.push(packTurn(k, to, c, P_GALE));
        }
      }
    }
  }
  return out;
}

// ── applying a ply ──────────────────────────────────────────────────────────────

export interface PlyEvent {
  kind: 'draft' | 'place' | 'move' | 'build' | 'dome' | 'shove' | 'win' | 'sealed';
  seat: number;
  from?: number;
  to?: number;
  cell?: number;
  level?: number;
  builder?: number;
  power?: number;
}

/**
 * Apply one ply. The caller must have checked it is in genPlies() — over the wire that check is the
 * whole security model, and it is done in duel.ts before this is ever reached.
 */
export function applyPly(s: State, ply: number, events?: PlyEvent[], checkSealed = true): void {
  const kind = plyKind(ply);
  const seat = s.turn;

  if (kind === K_DRAFT) {
    const idx = plyA(ply);
    const id = s.offer[idx];
    s.power[seat] = id;
    s.chg[seat] = chargesFor(id);
    s.offer = s.offer.filter((_, i) => i !== idx);
    events?.push({ kind: 'draft', seat, power: id });
    s.log.push(ply);
    s.ply++;
    s.turn = 1 - seat;
    if (s.power[0] !== P_NONE && s.power[1] !== P_NONE) {
      s.phase = PH_PLACE;
      s.turn = s.placeOrder[0];
    }
    return;
  }

  if (kind === K_PLACE) {
    const cell = plyA(ply);
    const placedSoFar = s.pos.reduce((n, p) => n + (p >= 0 ? 1 : 0), 0);
    const b = buildersOf(s, seat).find((i) => s.pos[i] < 0)!;
    s.pos[b] = cell;
    s.occ[cell] = b;
    events?.push({ kind: 'place', seat, cell, builder: b });
    s.log.push(ply);
    s.ply++;
    const next = placedSoFar + 1;
    const total = s.mode.builders * 2;
    if (next >= total) {
      s.phase = PH_PLAY;
      s.turn = s.firstMover;
    } else {
      s.turn = s.placeOrder[next % s.placeOrder.length];
    }
    return;
  }

  // ── a turn ──
  const k = plyBuilder(ply);
  const b = buildersOf(s, seat)[k];
  const to = plyA(ply);
  const build = plyBuild(ply);
  const pw = plyPower(ply);
  const aux = plyAux(ply);
  const from = s.pos[b];
  const st = s.stats[seat];

  st.turns++;
  if (pw !== P_NONE) {
    s.chg[seat]--;
    st.charges++;
  }

  if (pw === P_GALE && s.occ[to] !== -1) {
    const victim = s.occ[to];
    const push = beyond(s.w, s.h, from, to);
    s.occ[push] = victim;
    s.pos[victim] = push;
    events?.push({ kind: 'shove', seat, from: to, to: push, builder: victim });
  }

  s.occ[from] = -1;
  if (pw === P_COURIER && aux !== NONE) {
    events?.push({ kind: 'move', seat, from, to: aux, builder: b });
    events?.push({ kind: 'move', seat, from: aux, to, builder: b });
  } else {
    events?.push({ kind: 'move', seat, from, to, builder: b });
  }
  s.occ[to] = b;
  s.pos[b] = to;
  if (s.lev[to] > st.peak) st.peak = s.lev[to];
  if (s.lev[to] >= WIN_LEVEL - 1) st.highTurns++;

  if (build === NONE) {
    s.phase = PH_OVER;
    s.winner = seat;
    s.reason = 'climb';
    // The side "to move" at a terminal is always the LOSER, in both terminals, so the search can
    // score a leaf without asking which kind of ending it was.
    s.turn = 1 - seat;
    events?.push({ kind: 'win', seat, cell: to });
    s.log.push(ply);
    s.ply++;
    return;
  }

  const raise = (cell: number): void => {
    if (pw === P_WARDEN) {
      s.lev[cell] = DOME;
      st.domes++;
      events?.push({ kind: 'dome', seat, cell, level: DOME });
      return;
    }
    s.lev[cell] = (s.lev[cell] + 1) as number;
    st.blocks++;
    if (s.lev[cell] === DOME) {
      st.domes++;
      events?.push({ kind: 'dome', seat, cell, level: DOME });
    } else {
      events?.push({ kind: 'build', seat, cell, level: s.lev[cell] });
    }
  };
  raise(build);
  if (pw === P_MASON && aux !== NONE) {
    s.lev[aux] = (s.lev[aux] + 1) as number;
    s.stats[seat].blocks++;
    if (s.lev[aux] === DOME) {
      s.stats[seat].domes++;
      events?.push({ kind: 'dome', seat, cell: aux, level: DOME });
    } else {
      events?.push({ kind: 'build', seat, cell: aux, level: s.lev[aux] });
    }
  }

  s.log.push(ply);
  s.ply++;
  s.turn = 1 - seat;

  if (checkSealed && genPlies(s).length === 0) {
    s.phase = PH_OVER;
    s.winner = seat;
    s.reason = 'sealed';
    events?.push({ kind: 'sealed', seat: 1 - seat });
  }
}

/** Apply a ply and record the mobility the mover actually had. */
export function step(s: State, ply: number, events?: PlyEvent[]): void {
  if (s.phase === PH_PLAY) {
    const seat = s.turn;
    const n = genPlies(s).length;
    s.stats[seat].mobilitySum += n;
    s.stats[seat].mobilityN++;
  }
  applyPly(s, ply, events);
}

export const isOver = (s: State): boolean => s.phase === PH_OVER;

/** Total height on the board — the potential that proves the game terminates. */
export function totalHeight(s: State): number {
  let sum = 0;
  for (let i = 0; i < s.cells; i++) sum += s.lev[i];
  return sum;
}

export const maxPlies = (s: State): number => DOME * s.cells + s.mode.builders * 2 + 2;

// ── replaying a log (this is how a peer validates the wire) ──────────────────────

export interface ReplayResult {
  ok: boolean;
  state: State;
  /** Index of the first ply that was not legal, or -1. */
  badAt: number;
}

export function replay(modeId: string, seed: number, log: readonly number[]): ReplayResult {
  const s = createState(modeId, seed);
  for (let i = 0; i < log.length; i++) {
    if (isOver(s)) return { ok: false, state: s, badAt: i };
    const legal = genPlies(s);
    if (!legal.includes(log[i] >>> 0)) return { ok: false, state: s, badAt: i };
    step(s, log[i] >>> 0);
  }
  return { ok: true, state: s, badAt: -1 };
}

/** 32-bit FNV-1a over everything a peer must agree on. Peer ids and names are deliberately out. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (v >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  for (let i = 0; i < s.cells; i++) mix(s.lev[i]);
  for (let i = 0; i < s.pos.length; i++) mix(s.pos[i] + 2);
  mix(s.turn);
  mix(s.phase);
  mix(s.winner + 2);
  for (let i = 0; i < 2; i++) {
    mix(s.power[i]);
    mix(s.chg[i]);
  }
  mix(s.ply);
  return h >>> 0;
}

// ── helpers the UI and the bot share ────────────────────────────────────────────

/** The plies in `all` that move builder-of-seat index `k`. */
export function pliesForBuilder(all: readonly number[], k: number): number[] {
  return all.filter((p) => plyKind(p) === K_TURN && plyBuilder(p) === k);
}

export function destinationsFor(all: readonly number[], k: number): Set<number> {
  const out = new Set<number>();
  for (const p of all) if (plyKind(p) === K_TURN && plyBuilder(p) === k) out.add(plyA(p));
  return out;
}

export function buildsFor(all: readonly number[], k: number, to: number): Set<number> {
  const out = new Set<number>();
  for (const p of all) {
    if (plyKind(p) !== K_TURN || plyBuilder(p) !== k || plyA(p) !== to) continue;
    if (plyBuild(p) === NONE) continue;
    out.add(plyBuild(p));
  }
  return out;
}

/** Human-readable cell name, a1-style, for aria-labels and the move list. */
export function cellName(s: State, i: number): string {
  const x = i % s.w;
  const y = (i / s.w) | 0;
  return `${String.fromCharCode(97 + x)}${s.h - y}`;
}
